import { Capacitor } from "@capacitor/core";
import { supabase } from "./supabase";
import type { ActivityKind } from "./thera-data";
import type { LocalRouteDraft, LocalRoutePhoto } from "./route-submission-store";

export type SyncableRouteDraft = Omit<LocalRouteDraft, "storageKey" | "updatedAt">;

export type RouteGeoJson = {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: Record<string, string>;
    geometry:
      | { type: "LineString"; coordinates: number[][] }
      | { type: "Point"; coordinates: number[] };
  }>;
};

export type CloudRoute = {
  id: string;
  owner_id: string;
  name: string;
  area: string;
  kind: ActivityKind;
  description: string;
  recommendations: string;
  distance_m: number | null;
  ascent_m: number | null;
  estimated_seconds: number | null;
  path_geojson: RouteGeoJson | null;
  status: "draft" | "pending" | "published" | "rejected";
  visibility: "private" | "unlisted" | "public";
  source_platform: "web" | "android" | "import";
  created_at: string;
  updated_at: string;
};

export type PendingRoute = Pick<
  CloudRoute,
  "id" | "owner_id" | "name" | "area" | "kind" | "description" | "recommendations" | "distance_m" | "ascent_m" | "estimated_seconds" | "source_platform" | "created_at"
> & {
  owner_name: string;
  file_count: number;
  photo_count: number;
};

export function currentPlatform(): "web" | "android" {
  // PWA/normal browser are web. Only the real Capacitor Android APK is android.
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android"
    ? "android"
    : "web";
}

function requireSupabase() {
  if (!supabase) throw new Error("Supabase no está configurado.");
  return supabase;
}

async function currentUser() {
  const client = requireSupabase();
  const { data, error } = await client.auth.getUser();
  if (error) throw error;
  if (!data.user) throw new Error("Inicia sesión con una cuenta aprobada.");
  return data.user;
}

async function sha256(blob: Blob) {
  const bytes = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function uploadRouteFile(
  routeId: string,
  format: "gpx" | "geojson",
  blob: Blob,
) {
  const client = requireSupabase();
  const user = await currentUser();
  const extension = format === "gpx" ? "gpx" : "geojson";
  const objectPath = `${user.id}/${routeId}/route.${extension}`;
  const contentType = format === "gpx" ? "application/gpx+xml" : "application/geo+json";
  // Some browsers label downloaded GPX files as application/octet-stream.
  // Supabase validates the Blob MIME type against the private bucket, so
  // rebuild the Blob with the canonical type before uploading it.
  const typedBlob = blob.type === contentType
    ? blob
    : blob.slice(0, blob.size, contentType);
  const upload = await client.storage.from("thera-route-files").upload(objectPath, typedBlob, {
    contentType,
    cacheControl: "3600",
    upsert: true,
  });
  if (upload.error) throw upload.error;
  const metadata = await client.from("route_files").upsert(
    {
      route_id: routeId,
      owner_id: user.id,
      format,
      object_path: objectPath,
      size_bytes: typedBlob.size,
      checksum_sha256: await sha256(typedBlob),
    },
    { onConflict: "route_id,format" },
  );
  if (metadata.error) throw metadata.error;
}

async function uploadRoutePhoto(routeId: string, photo: LocalRoutePhoto) {
  const client = requireSupabase();
  const user = await currentUser();
  const objectPath = `${user.id}/${routeId}/${photo.id}.webp`;
  const upload = await client.storage.from("thera-photos").upload(objectPath, photo.blob, {
    contentType: "image/webp",
    cacheControl: "31536000",
    upsert: true,
  });
  if (upload.error) throw upload.error;
  const metadata = await client.from("photos").upsert(
    {
      owner_id: user.id,
      route_id: routeId,
      activity_id: null,
      client_photo_id: photo.id,
      object_path: objectPath,
      mime_type: "image/webp",
      width: photo.width,
      height: photo.height,
      size_bytes: photo.blob.size,
      alt_text: photo.altText,
      status: "draft",
    },
    { onConflict: "owner_id,client_photo_id" },
  );
  if (metadata.error) throw metadata.error;
}

export async function syncRouteDraft(draft: SyncableRouteDraft) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("save_route_draft", {
    p_client_draft_id: draft.id,
    p_name: draft.name,
    p_area: draft.area,
    p_kind: draft.kind,
    p_description: draft.description,
    p_recommendations: draft.recommendations,
    p_distance_m: draft.distanceM,
    p_ascent_m: draft.ascentM,
    p_estimated_seconds: draft.estimatedSeconds,
    p_path_geojson: draft.pathGeojson,
    p_source_platform: draft.sourcePlatform,
  });
  if (error) throw error;
  const routeId = String(data);

  if (draft.gpxFile) {
    await uploadRouteFile(routeId, "gpx", draft.gpxFile.blob);
  }
  await uploadRouteFile(
    routeId,
    "geojson",
    new Blob([JSON.stringify(draft.pathGeojson)], { type: "application/geo+json" }),
  );
  for (const photo of draft.photos) {
    await uploadRoutePhoto(routeId, photo);
  }
  return routeId;
}

export async function submitRoute(routeId: string) {
  const client = requireSupabase();
  const { error } = await client.rpc("submit_route_for_review", {
    p_route_id: routeId,
  });
  if (error) throw error;
}

const routeFields = "id,owner_id,name,area,kind,description,recommendations,distance_m,ascent_m,estimated_seconds,path_geojson,status,visibility,source_platform,created_at,updated_at";

export async function listOwnRoutes() {
  const client = requireSupabase();
  const user = await currentUser();
  const { data, error } = await client
    .from("routes")
    .select(routeFields)
    .eq("owner_id", user.id)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as CloudRoute[];
}

export async function listPublishedRoutes() {
  const client = requireSupabase();
  const { data, error } = await client
    .from("routes")
    .select(routeFields)
    .eq("status", "published")
    .eq("visibility", "public")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as CloudRoute[];
}

export async function listPendingRoutes() {
  const client = requireSupabase();
  const { data, error } = await client.rpc("admin_list_pending_routes");
  if (error) throw error;
  return (data ?? []) as PendingRoute[];
}

export async function reviewRoute(
  routeId: string,
  decision: "published" | "rejected",
) {
  const client = requireSupabase();
  const { error } = await client.rpc("admin_review_route", {
    p_route_id: routeId,
    p_decision: decision,
  });
  if (error) throw error;
}
